import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  trendingCategories,
  contentBlocks,
  socialCenterDashboardStats,
  livePulseItems,
  socialCenterRules,
  getHighestTrendingItem,
  type TrendingItem,
  type ContentBlock,
} from '@/mocks/rfwSocialCenter';

function TrendingItemWideCard({ item }: { item: TrendingItem }) {
  return (
    <Link
      to={item.link}
      className="flex flex-col sm:flex-row gap-4 p-5 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-400/50 transition-all duration-300 group"
    >
      <div className="relative w-full sm:w-44 h-40 sm:h-32 rounded-xl overflow-hidden shrink-0">
        <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 w-8 h-8 rounded-lg bg-background-50/90 flex items-center justify-center text-sm font-extrabold text-foreground-950 backdrop-blur-sm">
          #{item.position}
        </div>
        <div className="absolute bottom-3 left-3">
          <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-sm ${
            item.trendDirection === 'up' ? 'bg-emerald-500/80 text-white' : item.trendDirection === 'down' ? 'bg-rose-500/80 text-white' : 'bg-foreground-500/50 text-white'
          }`}>
            <i className={`${item.trendDirection === 'up' ? 'ri-arrow-up-line' : item.trendDirection === 'down' ? 'ri-arrow-down-line' : 'ri-subtract-line'} text-[9px]`}></i>
            {item.trendValue}
          </span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          {item.tags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium">{tag}</span>
          ))}
          <span className="ml-auto text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-flashlight-line text-amber-500 text-[9px]"></i>
            Score {item.score}
          </span>
        </div>
        <h3 className="text-sm font-extrabold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors duration-200">{item.name}</h3>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">{item.description}</p>
        <div className="flex items-center gap-4 text-[11px] text-foreground-400 flex-wrap">
          {item.stats.map((stat, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <strong className="text-foreground-700">{stat.value}</strong> {stat.label}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

function ContentBlockItemWideCard({ item, blockColor }: { item: ContentBlock['items'][0]; blockColor: string }) {
  return (
    <Link
      to={item.link}
      className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-amber-400/50 transition-all duration-300 group"
    >
      <div className="relative w-full sm:w-36 h-28 rounded-lg overflow-hidden shrink-0">
        <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover object-top" />
        <div className="absolute top-2 left-2 w-6 h-6 rounded-md bg-background-50/90 flex items-center justify-center text-[11px] font-extrabold text-foreground-950 backdrop-blur-sm">
          {item.position}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <span className="inline-block px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium mb-1.5">{item.category}</span>
        <h4 className="text-sm font-bold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors duration-200">{item.title}</h4>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-2">{item.description}</p>
        <div className="flex items-center gap-1 text-[11px]">
          <i className={`ri-bar-chart-fill text-amber-500 text-[9px]`}></i>
          <strong className="text-foreground-700">{item.metricValue.toLocaleString()}</strong>
          <span className="text-foreground-400">{item.metricLabel}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CentroSocialPage() {
  const [activeView, setActiveView] = useState<'trending' | 'bloques' | 'todo'>('todo');
  const [activeCategory, setActiveCategory] = useState<string>(trendingCategories[0].id);
  const [activeBlock, setActiveBlock] = useState<string>(contentBlocks[0].id);

  const selectedCategory = trendingCategories.find((c) => c.id === activeCategory) || trendingCategories[0];
  const selectedBlock = contentBlocks.find((b) => b.id === activeBlock) || contentBlocks[0];

  const views = [
    { id: 'todo' as const, label: 'Todo', icon: 'ri-global-line' },
    { id: 'trending' as const, label: 'Tendencias', icon: 'ri-fire-line' },
    { id: 'bloques' as const, label: 'Lo Más', icon: 'ri-bar-chart-grouped-line' },
  ];

  const showTrending = activeView === 'todo' || activeView === 'trending';
  const showBlocks = activeView === 'todo' || activeView === 'bloques';

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-primary-500 via-rose-500 to-orange-500 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.08) 0%, transparent 60%)' }}></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 mb-3">
            <Link to="/" className="text-white/60 hover:text-white text-xs transition-colors duration-200">
              RFW
            </Link>
            <i className="ri-arrow-right-s-line text-white/40 text-xs"></i>
            <span className="text-white/80 text-xs font-medium">Centro Social</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-white mb-3">
                Centro <span className="text-amber-200">Social</span> RFW
              </h1>
              <p className="text-white/70 max-w-xl text-sm md:text-base mb-4">
                El punto de encuentro de toda la comunidad radial. Tendencias en tiempo real, lo más comentado, compartido, reaccionado, guardado y visitado. Todo se actualiza automáticamente según la actividad real.
              </p>
              <div className="flex items-center gap-3 text-white/60 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  En vivo · Actualizado hace {socialCenterDashboardStats.updatedMinutesAgo} min
                </span>
                <span>{socialCenterDashboardStats.totalTrendingItems.toLocaleString()} items en tendencia</span>
                <span>{socialCenterDashboardStats.activeCategories} categorías</span>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Link
                to="/rankings"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-all duration-200 whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-trophy-line text-[10px]"></i>
                Rankings Globales
              </Link>
              <Link
                to="/explorar"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-medium transition-all duration-200 whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-compass-line text-[10px]"></i>
                Explorar Todo
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Live Pulse Bar */}
      <div className="bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {livePulseItems.map((pulse, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className={`w-7 h-7 rounded-lg ${pulse.bgColor} flex items-center justify-center shrink-0`}>
                  <i className={`${pulse.icon} ${pulse.color} text-xs`}></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground-950">{pulse.value}</p>
                  <p className="text-[9px] text-foreground-500">{pulse.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Views + Filters */}
      <div className="bg-white border-b border-background-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
              {views.map((view) => (
                <button
                  key={view.id}
                  onClick={() => setActiveView(view.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeView === view.id
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${view.icon} text-[10px]`}></i>
                  {view.label}
                </button>
              ))}
            </div>

            {showTrending && activeView !== 'todo' && (
              <>
                <span className="hidden md:block w-px h-6 bg-background-200"></span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {trendingCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeCategory === cat.id
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200/70'
                      }`}
                    >
                      <i className={`${cat.icon} text-[9px]`}></i>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            {showBlocks && activeView !== 'todo' && (
              <>
                <span className="hidden md:block w-px h-6 bg-background-200"></span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {contentBlocks.map((block) => (
                    <button
                      key={block.id}
                      onClick={() => setActiveBlock(block.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeBlock === block.id
                          ? 'bg-accent-500 text-white shadow-sm'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200/70'
                      }`}
                    >
                      <i className={`${block.icon} text-[9px]`}></i>
                      {block.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col xl:flex-row gap-6">

          {/* MAIN */}
          <div className="flex-1 min-w-0 space-y-8">

            {/* TRENDING */}
            {showTrending && (
              <div>
                {activeView === 'todo' ? (
                  trendingCategories.map((cat) => (
                    <div key={cat.id} className="mb-10">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-8 h-8 rounded-lg ${cat.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
                          <i className={`${cat.icon} ${cat.color} text-base`}></i>
                        </div>
                        <div>
                          <h2 className="text-lg font-extrabold text-foreground-950">{cat.name}</h2>
                          <p className="text-[10px] text-foreground-500">{cat.totalItems.toLocaleString()} items · {cat.description}</p>
                        </div>
                        <Link
                          to={`/centro-social#${cat.id}`}
                          onClick={() => { setActiveView('trending'); setActiveCategory(cat.id); }}
                          className="ml-auto text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 shrink-0 whitespace-nowrap"
                        >
                          Ver todo <i className="ri-arrow-right-line text-[9px]"></i>
                        </Link>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {cat.items.map((item) => (
                          <TrendingItemWideCard key={item.id} item={item} />
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-8 h-8 rounded-lg ${selectedCategory.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
                        <i className={`${selectedCategory.icon} ${selectedCategory.color} text-base`}></i>
                      </div>
                      <div>
                        <h2 className="text-lg font-extrabold text-foreground-950">{selectedCategory.name}</h2>
                        <p className="text-[10px] text-foreground-500">{selectedCategory.totalItems.toLocaleString()} items totales</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {selectedCategory.items.map((item) => (
                        <TrendingItemWideCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CONTENT BLOCKS */}
            {showBlocks && (
              <div>
                {activeView === 'todo' ? (
                  contentBlocks.map((block) => (
                    <div key={block.id} className="mb-10">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-8 h-8 rounded-lg ${block.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
                          <i className={`${block.icon} ${block.color} text-base`}></i>
                        </div>
                        <div>
                          <h2 className="text-lg font-extrabold text-foreground-950">{block.name}</h2>
                          <p className="text-[10px] text-foreground-500">{block.description}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {block.items.map((item) => (
                          <ContentBlockItemWideCard key={item.id} item={item} blockColor={block.color} />
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-8 h-8 rounded-lg ${selectedBlock.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
                        <i className={`${selectedBlock.icon} ${selectedBlock.color} text-base`}></i>
                      </div>
                      <div>
                        <h2 className="text-lg font-extrabold text-foreground-950">{selectedBlock.name}</h2>
                        <p className="text-[10px] text-foreground-500">{selectedBlock.description}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {selectedBlock.items.map((item) => (
                        <ContentBlockItemWideCard key={item.id} item={item} blockColor={selectedBlock.color} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!showTrending && !showBlocks && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">Seleccioná una vista para explorar el contenido.</p>
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR */}
          <aside className="xl:w-72 shrink-0 space-y-4">
            {/* Stats Card */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h3 className="text-xs font-extrabold text-foreground-400 uppercase tracking-wider mb-4">Dashboard Social</h3>
              <div className="space-y-3">
                {[
                  { label: 'Total Tendencias', value: socialCenterDashboardStats.totalTrendingItems.toLocaleString(), icon: 'ri-fire-line', color: 'text-rose-600' },
                  { label: 'Categorías Activas', value: socialCenterDashboardStats.activeCategories, icon: 'ri-price-tag-3-line', color: 'text-amber-600' },
                  { label: 'Score Promedio', value: socialCenterDashboardStats.avgTrendScore, icon: 'ri-line-chart-line', color: 'text-emerald-600' },
                  { label: 'Más Activa', value: socialCenterDashboardStats.mostActiveCategory, icon: 'ri-trophy-line', color: 'text-violet-600', sub: socialCenterDashboardStats.mostActiveCategoryCount.toLocaleString() + ' items' },
                  { label: 'Engagement Total', value: (socialCenterDashboardStats.totalEngagementActions / 1000).toFixed(1) + 'K', icon: 'ri-flashlight-line', color: 'text-orange-600' },
                  { label: 'Nuevas Esta Semana', value: socialCenterDashboardStats.trendingThisWeek.toLocaleString(), icon: 'ri-arrow-up-circle-line', color: 'text-sky-600' },
                ].map((stat, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-lg ${stat.color.replace('text-', 'bg-').replace('600', '50')} flex items-center justify-center shrink-0`}>
                      <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground-950">{stat.value}</p>
                      <p className="text-[9px] text-foreground-500">{stat.label}{stat.sub ? ` · ${stat.sub}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Explore links */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h3 className="text-xs font-extrabold text-foreground-400 uppercase tracking-wider mb-3">Explorar</h3>
              <nav className="space-y-0.5">
                {trendingCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveView('trending'); setActiveCategory(cat.id); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-all cursor-pointer"
                  >
                    <i className={`${cat.icon} ${cat.color} text-sm`}></i>
                    {cat.name}
                    <span className="ml-auto text-[9px] text-foreground-400">{cat.totalItems.toLocaleString()}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Rules */}
            <div className="p-5 rounded-2xl bg-accent-50 border border-accent-200/50">
              <h3 className="text-xs font-extrabold text-foreground-400 uppercase tracking-wider mb-3">¿Cómo funciona?</h3>
              <div className="space-y-3">
                {socialCenterRules.map((rule, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${rule.icon} text-accent-600 text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-foreground-950">{rule.title}</p>
                      <p className="text-[10px] text-foreground-500 leading-relaxed">{rule.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h3 className="text-xs font-extrabold text-foreground-400 uppercase tracking-wider mb-3">Atajos</h3>
              <div className="space-y-1">
                {[
                  { to: '/rankings', label: 'Rankings Globales', icon: 'ri-trophy-line' },
                  { to: '/centro-notificaciones', label: 'Notificaciones', icon: 'ri-notification-3-line' },
                  { to: '/seguir-usuarios', label: 'Red Social', icon: 'ri-group-line' },
                  { to: '/clubes', label: 'Clubes', icon: 'ri-shield-line' },
                  { to: '/eventos', label: 'Eventos', icon: 'ri-calendar-event-line' },
                  { to: '/competencias', label: 'Concursos', icon: 'ri-gift-line' },
                  { to: '/gamificacion', label: 'Gamificación', icon: 'ri-flashlight-line' },
                  { to: '/explorar', label: 'Explorar Radios', icon: 'ri-compass-line' },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs text-foreground-500 hover:text-foreground-900 hover:bg-background-100 transition-all"
                  >
                    <i className={link.icon}></i>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-flashlight-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Todo debe actualizarse automáticamente según actividad real.
          </p>
          <p className="text-xs text-foreground-600 max-w-xl mx-auto">
            El Centro Social RFW es el reflejo vivo de lo que la comunidad está haciendo ahora mismo. Cada like, cada comentario, cada compartir, cada visita — todo suma y modifica lo que ves en tiempo real. Lo que está en tendencia es lo que realmente está pasando. No hay curaduría manual, no hay edición, no hay demoras. La verdad de la comunidad, reflejada al instante. Este es el corazón palpitante de RadioFrecuenciaWEB.
          </p>
        </div>

        {/* Cross-links */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8 pt-6 border-t border-background-100">
          <span className="text-xs text-foreground-400">También te puede interesar:</span>
          {[
            { to: '/rankings', label: 'Rankings RFW', icon: 'ri-trophy-line' },
            { to: '/centro-notificaciones', label: 'Centro de Notificaciones', icon: 'ri-notification-3-line' },
            { to: '/seguir-usuarios', label: 'Seguir Usuarios', icon: 'ri-user-add-line' },
            { to: '/perfil-social-oyente', label: 'Perfil Social', icon: 'ri-user-line' },
            { to: '/clubes', label: 'Clubes', icon: 'ri-shield-line' },
            { to: '/gamificacion', label: 'Gamificación', icon: 'ri-flashlight-line' },
          ].map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 text-xs font-medium transition-all duration-200 whitespace-nowrap"
            >
              <i className={`${link.icon} text-[10px]`}></i>
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}